import type { NotificationItem } from '@hemmafixa/shared';
import { Button, Card, EmptyState, FilterChips, PageHeader, Pagination, cn } from '@hemmafixa/ui';
import { Bell } from 'lucide-react';
import { useState } from 'react';
import { useNavigate } from 'react-router';
import { fill, useI18n } from '@/i18n/context';
import { QueryView } from '../shared/query-view';
import { useMarkRead, useNotifications, useUnreadCount } from './api';
import { useDescribeNotification } from './describe';

type Filter = 'all' | 'unread';
const PAGE_SIZE = 20;

/** Every notification for the signed-in user, newest first (P4-08). */
export function NotificationsPage() {
  const { t, relative, dateTime } = useI18n();
  const n = t.notifications;
  const navigate = useNavigate();
  const describe = useDescribeNotification();
  const [filter, setFilter] = useState<Filter>('all');
  const [page, setPage] = useState(1);
  const query = useNotifications({ unread: filter === 'unread', page, pageSize: PAGE_SIZE });
  const count = useUnreadCount().data?.count ?? 0;
  const markRead = useMarkRead();

  const open = (item: NotificationItem) => {
    if (!item.readAt) markRead.mutate({ ids: [item.id] });
    navigate(describe(item).href);
  };

  return (
    <div className="mx-auto grid w-full max-w-3xl gap-4">
      <PageHeader
        title={n.title}
        description={n.description}
        actions={
          count > 0 && (
            <Button
              variant="outline"
              onClick={() => markRead.mutate({ all: true })}
              loading={markRead.isPending}
            >
              {n.markAllRead}
            </Button>
          )
        }
      />
      <FilterChips
        label={n.show}
        value={filter}
        onChange={(value) => {
          setFilter(value);
          setPage(1);
        }}
        options={[
          { value: 'all' as const, label: n.all },
          { value: 'unread' as const, label: n.unreadFilter, count },
        ]}
      />
      <QueryView query={query}>
        {(result) =>
          result.items.length === 0 ? (
            <EmptyState
              icon={Bell}
              title={filter === 'unread' ? n.noUnread : n.empty}
              description={n.emptyText}
            />
          ) : (
            <>
              <Card className="divide-y divide-border p-0">
                <ul>
                  {result.items.map((item) => {
                    const view = describe(item);
                    const unread = !item.readAt;
                    return (
                      <li key={item.id}>
                        <button
                          type="button"
                          onClick={() => open(item)}
                          className="flex w-full items-start gap-3 px-4 py-3.5 text-left transition-colors hover:bg-muted focus-visible:outline-2 focus-visible:outline-ring"
                        >
                          <span
                            aria-hidden
                            className={cn(
                              'mt-1.5 size-2 shrink-0 rounded-full',
                              unread ? 'bg-primary' : 'bg-transparent',
                            )}
                          />
                          <span className="grid min-w-0 flex-1 gap-0.5">
                            <span className={cn('text-sm', unread && 'font-semibold')}>
                              {view.title}
                              {unread && <span className="sr-only">, {n.unread}</span>}
                            </span>
                            <span className="truncate text-sm text-muted-foreground">{view.detail}</span>
                          </span>
                          <time
                            dateTime={item.createdAt}
                            title={dateTime(item.createdAt)}
                            className="shrink-0 text-xs text-muted-foreground"
                          >
                            {relative(item.createdAt)}
                          </time>
                        </button>
                      </li>
                    );
                  })}
                </ul>
              </Card>
              <Pagination
                page={result.page}
                pageSize={result.pageSize}
                total={result.total}
                onPageChange={setPage}
                labels={{
                  previous: t.common.previous,
                  next: t.common.next,
                  showing: (from, to, total) => fill(t.common.showingRange, { from, to, total }),
                }}
              />
            </>
          )
        }
      </QueryView>
    </div>
  );
}
